import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, Link } from "react-router-dom";
import {
  PlayCircle,
  Lock,
  LockOpen,
  DownloadSimple,
  PencilSimple,
  Trash,
  Plus,
  X,
  Users,
  Robot,
  ChatCircle,
  ChatsCircle,
  GridFour,
  ListBullets,
  Gauge,
  ArrowUpRight,
  Sparkle,
  MagnifyingGlass,
  CheckCircle,
  ShieldCheck,
  GraduationCap,
  Download,
  Key,
  WhatsappLogo,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadBlob } from "@/lib/bootcampFiles";
import { NicheProfileCards } from "@/components/NicheDocStrip";
import { BootcampsAdminSection, UserBootcampsTab } from "@/components/BootcampsAdmin";
import { UserChatsTab } from "@/components/UserChatsTab";
import { ResourcesAdminSection } from "@/components/ResourcesAdmin";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";

/* -------------------------------------------------------------------------- */
/*  Layout                                                                    */
/* -------------------------------------------------------------------------- */

export default function Admin() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const isMaster = !!user?.is_master_admin;
  // Regular admins land on Users (their only allowed section); masters land
  // on Overview as before.
  const [section, setSection] = useState(isMaster ? "overview" : "users");
  const [stats, setStats] = useState(null);
  const [gpts, setGpts] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setUsersLoading(true);
    setUsersError("");
    api.get("/admin/users")
      .then(({ data }) => setUsers(data))
      .catch((e) => setUsersError(e.response?.data?.detail || "Couldn't load users. Please refresh."))
      .finally(() => setUsersLoading(false));
    if (isMaster) {
      api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
      api.get("/gpts").then(({ data }) => setGpts(data)).catch(() => {});
    }
  }, [refreshKey, isMaster]);

  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <div className="admin-shell" data-testid="admin-page">
      <Sidebar section={section} setSection={setSection} user={user} onLogout={logout} theme={theme} toggle={toggle} isMaster={isMaster} />

      <main className="admin-main">
        <TopBar user={user} />
        <div className="admin-content">
          {isMaster && section === "overview" && (
            <Overview stats={stats} gpts={gpts} users={users} setSection={setSection} />
          )}
          {isMaster && section === "gpts" && <GPTsSection gpts={gpts} onChanged={refresh} />}
          {section === "users" && (
            <UsersSection users={users} loading={usersLoading} loadError={usersError} currentUserId={user?.id} onChanged={refresh} isMaster={isMaster} />
          )}
          {isMaster && section === "resets" && <PasswordResetsSection />}
          {section === "bootcamps" && <BootcampsAdminSection />}
          {section === "resources" && <ResourcesAdminSection />}
        </div>
      </main>
    </div>
  );
}

function Sidebar({ section, setSection, user, onLogout, theme, toggle, isMaster }) {
  const allItems = [
    { key: "overview", label: "Overview", icon: <Gauge size={18} weight="duotone" />, testid: "admin-nav-overview", masterOnly: true },
    { key: "gpts", label: "Custom GPTs", icon: <Robot size={18} weight="duotone" />, testid: "admin-tab-gpts", masterOnly: true },
    { key: "users", label: "Users", icon: <Users size={18} weight="duotone" />, testid: "admin-tab-users", masterOnly: false },
    { key: "bootcamps", label: "Bootcamps", icon: <GraduationCap size={18} weight="duotone" />, testid: "admin-tab-bootcamps", masterOnly: false },
    { key: "resources", label: "Resources", icon: <PlayCircle size={18} weight="duotone" />, testid: "admin-tab-resources", masterOnly: false },
    { key: "resets", label: "Password Resets", icon: <Key size={18} weight="duotone" />, testid: "admin-tab-resets", masterOnly: true },
  ];
  const items = allItems.filter((it) => isMaster || !it.masterOnly);

  return (
    <aside className="admin-sidebar" data-testid="admin-sidebar">
      <div className="admin-sidebar-inner">
        <Link to="/" className="admin-brand" data-testid="admin-brand">
          <img src="/adxsk-logo.png" alt="Akshat Dani x SK" className="admin-brand-logo admin-brand-logo-light" />
          <img src="/adxsk-logo-dark.png" alt="Akshat Dani x SK" className="admin-brand-logo admin-brand-logo-dark" />
          <div className="admin-brand-sub-wrap">
            <div className="admin-brand-sub">Control Room</div>
          </div>
        </Link>

        <div className="admin-side-section">
          <p className="label-uppercase admin-side-label">Workspace</p>
          <nav className="admin-nav">
            {items.map((it) => (
              <button
                key={it.key}
                onClick={() => setSection(it.key)}
                className={`admin-nav-item ${section === it.key ? "is-active" : ""}`}
                data-testid={it.testid}
              >
                <span className="admin-nav-icon">{it.icon}</span>
                <span>{it.label}</span>
                {section === it.key && <span className="admin-nav-dot" />}
              </button>
            ))}
          </nav>
        </div>

        <div className="admin-side-section">
          <p className="label-uppercase admin-side-label">Shortcut</p>
          <NavLink to="/" className="admin-nav-item" data-testid="admin-go-userside">
            <span className="admin-nav-icon"><ArrowUpRight size={18} weight="duotone" /></span>
            <span>User experience</span>
          </NavLink>
        </div>

        <div className="admin-side-foot">
          <button onClick={toggle} className="btn btn-ghost btn-sm" data-testid="admin-theme-toggle">
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </button>
          <div className="admin-user-card">
            <div className="admin-user-avatar">{(user?.name || "A").slice(0, 1).toUpperCase()}</div>
            <div className="admin-user-meta">
              <div className="admin-user-name">
                {user?.name}{" "}
                {user?.is_master_admin && (
                  <span className="chip chip-success" style={{ fontSize: "0.6rem", marginLeft: "0.35rem" }} data-testid="master-admin-badge">
                    MASTER
                  </span>
                )}
              </div>
              <div className="admin-user-mail">{user?.email}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm w-full" onClick={onLogout} data-testid="admin-logout-button">Sign out</button>
        </div>
      </div>
    </aside>
  );
}

function TopBar({ user }) {
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  return (
    <div className="admin-topbar" data-testid="admin-topbar">
      <div>
        <p className="label-uppercase" style={{ color: "var(--accent)" }}>{today}</p>
        <h1 className="admin-greeting">
          Welcome back, {user?.name?.split(" ")[0] || "Admin"}.
        </h1>
      </div>
      <div className="admin-topbar-actions">
        <div className="admin-search">
          <MagnifyingGlass size={16} weight="bold" />
          <input placeholder="Search GPTs, users..." disabled data-testid="admin-search-input" />
        </div>
        <span className="chip admin-role-chip"><ShieldCheck size={12} weight="bold" /> Admin</span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Overview                                                                  */
/* -------------------------------------------------------------------------- */

function BootcampsOverviewCard({ setSection }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/admin/bootcamps-stats`).then(({ data }) => setRows(data.bootcamps)).catch(() => setRows([])); }, []);
  return (
    <section className="panel" data-testid="admin-overview-bootcamps">
      <div className="panel-head">
        <div><p className="label-uppercase">Mentorship</p><h3 className="panel-title">Bootcamps</h3></div>
        <button onClick={() => setSection("bootcamps")} className="btn btn-ghost btn-sm" data-testid="admin-overview-manage-bootcamps">Manage <ArrowUpRight size={14} /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4">
        {(rows || []).map((b) => (
          <div key={b.number} className="niche-card" data-testid={`overview-bootcamp-${b.number}`}>
            <p className="text-xs font-semibold" style={{ color: "var(--foreground)" }}>Bootcamp {b.number}</p>
            <p className="text-xs" style={{ color: "var(--muted)" }}>{b.submitted}/{b.participants} submitted</p>
            <p className="text-xs" style={{ color: "var(--muted)" }}>{b.completed} completed · {b.files} files</p>
          </div>
        ))}
        {rows === null && <span className="spinner" />}
      </div>
    </section>
  );
}

function HealthCard() {
  const [h, setH] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/admin/health`).then(({ data }) => { if (alive) { setH(data); setErr(false); } }).catch(() => { if (alive) setErr(true); });
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const status = err ? "red" : h?.status || "grey";
  const label = { green: "Healthy", amber: "Watch", red: "Attention", grey: "Loading…" }[status];
  const errs = h?.errors || {};
  const causes = [
    errs.out_of_credit && "OpenAI credit exhausted — top up now",
    errs.capacity && `${errs.capacity} hit full capacity`,
    errs.provider_busy && `${errs.provider_busy} provider busy`,
    errs.timeout && `${errs.timeout} timed out`,
    errs.error && `${errs.error} other errors`,
    h?.fallbacks && `${h.fallbacks} served by GPT-5.5 backup (Claude unavailable)`,
    err && "Health feed unreachable",
  ].filter(Boolean);
  return (
    <section className="panel health-card" data-testid="admin-health-card" data-status={status}>
      <div className="panel-head">
        <div><p className="label-uppercase">Live session</p><h3 className="panel-title">AI health</h3></div>
        <span className="health-light" data-testid="admin-health-status"><i className={`health-dot health-dot-${status}`} />{label}</span>
      </div>
      <div className="health-body">
        <div className="health-big">
          <div><span className="health-num" data-testid="admin-health-pool">{h ? `${h.pool.active}/${h.pool.max}` : "—"}</span><span className="health-lbl">AI slots in use{h?.pool.servers > 1 ? ` · ${h.pool.servers} servers` : ""}</span></div>
          <div><span className="health-num" data-testid="admin-health-waiting">{h ? h.pool.waiting : "—"}</span><span className="health-lbl">waiting{h?.pool.longest_wait_s > 0 ? ` · longest ${h.pool.longest_wait_s}s` : ""}</span></div>
        </div>
        <div className="health-row">
          <span>Last {h?.window_minutes ?? 15} min</span>
          <span data-testid="admin-health-served">{h ? `${h.served} replies` : "—"}</span>
          <span data-testid="admin-health-timing">{h?.avg_reply_s != null ? `avg ${h.avg_reply_s}s · slowest ${h.slowest_reply_s}s` : "no replies yet"}</span>
        </div>
        <p className="health-provider" data-testid="admin-health-provider">
          {h ? `4-3-2-1 served by Claude Haiku 5.5${h.fallbacks ? ` · ${h.fallbacks} fell back to GPT-5.5` : " · 0 fallbacks"}` : "—"}
        </p>
        <ul className="health-causes" data-testid="admin-health-causes">
          {causes.length === 0 ? <li className="health-ok">No errors in the last {h?.window_minutes ?? 15} minutes</li> : causes.map((c) => <li key={c}>{c}</li>)}
        </ul>
      </div>
    </section>
  );
}

function Overview({ stats, gpts, users, setSection }) {
  return (
    <div className="space-y-10 fade-up" data-testid="admin-overview">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <HealthCard />
        <div className="lg:col-span-2"><BootcampsOverviewCard setSection={setSection} /></div>
      </div>
      {/* KPI grid */}
      <section>
        <SectionHeader
          eyebrow="Pulse"
          title="Today at a glance"
          subtitle="Live snapshot of your consultation hub."
        />
        <div className="kpi-grid" data-testid="admin-stats">
          <KpiTile
            label="Total Users"
            value={stats?.total_users ?? "—"}
            icon={<Users size={20} weight="duotone" />}
            tone="primary"
            hint="Across all roles"
          />
          <KpiTile
            label="Active GPTs"
            value={stats ? stats.total_gpts - stats.locked_gpts : "—"}
            icon={<Robot size={20} weight="duotone" />}
            tone="accent"
            hint={stats ? `${stats.total_gpts} total · ${stats.locked_gpts} locked` : ""}
          />
          <KpiTile
            label="Conversations"
            value={stats?.total_conversations ?? "—"}
            icon={<ChatsCircle size={20} weight="duotone" />}
            tone="dark"
            hint="Lifetime"
          />
          <KpiTile
            label="Messages"
            value={stats?.total_messages ?? "—"}
            icon={<ChatCircle size={20} weight="duotone" />}
            tone="soft"
            hint="User + assistant"
          />
        </div>
      </section>

      {/* Two-column: recent GPTs + recent users */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 panel">
          <div className="panel-head">
            <div>
              <p className="label-uppercase" style={{ color: "var(--accent)" }}>Custom GPTs</p>
              <h3 className="panel-title">Your consultation lineup</h3>
            </div>
            <button onClick={() => setSection("gpts")} className="btn btn-ghost btn-sm" data-testid="admin-overview-manage-gpts">
              Manage <ArrowUpRight size={14} weight="bold" />
            </button>
          </div>
          <div className="panel-body">
            {gpts.length === 0 ? (
              <EmptyHint label="No GPTs yet" />
            ) : (
              <div className="gpt-mini-grid">
                {gpts.slice(0, 6).map((g) => (
                  <div key={g.id} className="gpt-mini-card" data-testid={`admin-overview-gpt-${g.id}`}>
                    <div className="gpt-mini-avatar">
                      {g.avatar_url ? (
                        <img src={g.avatar_url} alt="" />
                      ) : (
                        <Robot size={18} weight="duotone" />
                      )}
                    </div>
                    <div className="gpt-mini-meta">
                      <div className="gpt-mini-title">{g.name}</div>
                      <div className="gpt-mini-cat">{g.category}</div>
                    </div>
                    {g.locked ? (
                      <span className="dot dot-locked" title="Locked" />
                    ) : (
                      <span className="dot dot-active" title="Active" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <div>
              <p className="label-uppercase" style={{ color: "var(--accent)" }}>Members</p>
              <h3 className="panel-title">Recent users</h3>
            </div>
            <button onClick={() => setSection("users")} className="btn btn-ghost btn-sm" data-testid="admin-overview-manage-users">
              Manage <ArrowUpRight size={14} weight="bold" />
            </button>
          </div>
          <div className="panel-body">
            {users.length === 0 ? (
              <EmptyHint label="No users yet" />
            ) : (
              <ul className="user-list">
                {users.slice(0, 5).map((u) => (
                  <li key={u.id} className="user-list-item" data-testid={`admin-overview-user-${u.id}`}>
                    <div className="user-avatar">{u.name.slice(0, 1).toUpperCase()}</div>
                    <div className="user-meta">
                      <div className="user-name">{u.name}</div>
                      <div className="user-mail">{u.email}</div>
                    </div>
                    {u.role === "admin" ? (
                      <span className="chip">Admin</span>
                    ) : (
                      <span className="chip chip-muted">User</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <section className="panel quick-actions" data-testid="admin-quick-actions">
        <div className="panel-head">
          <div>
            <p className="label-uppercase" style={{ color: "var(--accent)" }}>Shortcuts</p>
            <h3 className="panel-title">Quick actions</h3>
          </div>
        </div>
        <div className="quick-grid">
          <QuickAction
            icon={<Sparkle size={18} weight="duotone" />}
            title="Create a GPT"
            desc="Spin up a new consultation persona."
            onClick={() => setSection("gpts")}
            testid="quick-action-new-gpt"
          />
          <QuickAction
            icon={<Lock size={18} weight="duotone" />}
            title="Lock / unlock"
            desc="Manage user access per GPT."
            onClick={() => setSection("gpts")}
            testid="quick-action-locks"
          />
          <QuickAction
            icon={<Users size={18} weight="duotone" />}
            title="Promote a user"
            desc="Grant admin access to a teammate."
            onClick={() => setSection("users")}
            testid="quick-action-users"
          />
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ eyebrow, title, subtitle, action }) {
  return (
    <div className="section-head">
      <div>
        <p className="label-uppercase" style={{ color: "var(--accent)" }}>{eyebrow}</p>
        <h2 className="section-title">{title}</h2>
        {subtitle && <p className="section-sub">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function KpiTile({ label, value, icon, tone, hint }) {
  return (
    <div className={`kpi-tile kpi-${tone}`} data-testid={`admin-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="kpi-icon">{icon}</div>
      <div className="kpi-meta">
        <div className="kpi-label">{label}</div>
        <div className="kpi-value">{value}</div>
        {hint && <div className="kpi-hint">{hint}</div>}
      </div>
    </div>
  );
}

function QuickAction({ icon, title, desc, onClick, testid }) {
  return (
    <button onClick={onClick} className="quick-card" data-testid={testid}>
      <div className="quick-icon">{icon}</div>
      <div className="quick-meta">
        <div className="quick-title">{title}</div>
        <div className="quick-desc">{desc}</div>
      </div>
      <ArrowUpRight size={14} weight="bold" className="quick-arrow" />
    </button>
  );
}

function EmptyHint({ label }) {
  return (
    <div className="empty-hint">
      <CheckCircle size={18} weight="duotone" />
      <span>{label}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  GPTs section                                                              */
/* -------------------------------------------------------------------------- */

function GPTsSection({ gpts, onChanged }) {
  const [view, setView] = useState("grid");
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const filtered = gpts.filter(
    (g) =>
      g.name.toLowerCase().includes(query.toLowerCase()) ||
      g.category.toLowerCase().includes(query.toLowerCase())
  );

  const toggleLock = async (g) => {
    try {
      await api.patch(`/gpts/${g.id}`, { locked: !g.locked });
      onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const onDelete = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? This will remove all related conversations.`)) return;
    try {
      await api.delete(`/gpts/${g.id}`);
      onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="fade-up space-y-6" data-testid="admin-gpts-tab">
      <SectionHeader
        eyebrow="Library"
        title="Custom GPTs"
        subtitle="Create, edit, and gate access to your consultation personas."
        action={
          <button onClick={() => setEditing("new")} className="btn btn-accent" data-testid="admin-new-gpt-button">
            <Plus size={16} weight="bold" /> New GPT
          </button>
        }
      />

      <div className="toolbar">
        <div className="admin-search w-full max-w-md">
          <MagnifyingGlass size={16} weight="bold" />
          <input
            placeholder="Search by name or category..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-gpts-search"
          />
        </div>
        <div className="seg">
          <button
            className={`seg-btn ${view === "grid" ? "is-on" : ""}`}
            onClick={() => setView("grid")}
            data-testid="admin-view-grid"
            aria-label="Grid view"
          >
            <GridFour size={14} weight="bold" />
          </button>
          <button
            className={`seg-btn ${view === "list" ? "is-on" : ""}`}
            onClick={() => setView("list")}
            data-testid="admin-view-list"
            aria-label="List view"
          >
            <ListBullets size={14} weight="bold" />
          </button>
        </div>
      </div>

      {error && <p className="error-banner" data-testid="admin-gpts-error">{error}</p>}

      {view === "grid" ? (
        <div className="gpt-card-grid">
          {filtered.map((g) => (
            <AdminGPTCard
              key={g.id}
              g={g}
              onLock={() => toggleLock(g)}
              onEdit={() => setEditing(g)}
              onDelete={() => onDelete(g)}
            />
          ))}
          {filtered.length === 0 && (
            <div className="empty-state col-span-full" data-testid="admin-gpts-empty">
              <Robot size={36} weight="duotone" />
              <p>No GPTs found.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="grid-frame overflow-x-auto">
          <table className="adm-table" data-testid="admin-gpts-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Status</th>
                <th style={{ width: 1 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <tr key={g.id} data-testid={`admin-gpt-row-${g.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="gpt-mini-avatar small">
                        {g.avatar_url ? <img src={g.avatar_url} alt="" /> : <Robot size={16} weight="duotone" />}
                      </div>
                      <div>
                        <div className="font-semibold">{g.name}</div>
                        <div className="text-xs" style={{ color: "var(--muted)" }}>{g.description}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="chip-muted chip">{g.category}</span></td>
                  <td>
                    {g.locked ? (
                      <span className="chip chip-locked"><Lock size={10} weight="bold" />&nbsp;Locked</span>
                    ) : (
                      <span className="chip chip-success"><CheckCircle size={10} weight="bold" />&nbsp;Active</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => toggleLock(g)} className="btn btn-ghost btn-sm" data-testid={`admin-gpt-table-lock-toggle-${g.id}`}>
                        {g.locked ? <LockOpen size={14} weight="duotone" /> : <Lock size={14} weight="duotone" />}
                      </button>
                      <button onClick={() => setEditing(g)} className="btn btn-ghost btn-sm" data-testid={`admin-gpt-edit-${g.id}`}>
                        <PencilSimple size={14} weight="duotone" />
                      </button>
                      <button onClick={() => onDelete(g)} className="btn btn-danger btn-sm" data-testid={`admin-gpt-delete-${g.id}`}>
                        <Trash size={14} weight="duotone" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-8" style={{ color: "var(--muted)" }}>
                    No GPTs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <GPTEditor
          gpt={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }}
        />
      )}
    </div>
  );
}

function AdminGPTCard({ g, onLock, onEdit, onDelete }) {
  return (
    <div className="admin-gpt-card" data-testid={`admin-gpt-row-${g.id}`}>
      <div className="admin-gpt-card-head">
        <div className="gpt-mini-avatar">
          {g.avatar_url ? <img src={g.avatar_url} alt="" /> : <Robot size={20} weight="duotone" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="chip chip-muted">{g.category}</span>
            {g.locked ? (
              <span className="chip chip-locked"><Lock size={10} weight="bold" />&nbsp;Locked</span>
            ) : (
              <span className="chip chip-success"><CheckCircle size={10} weight="bold" />&nbsp;Active</span>
            )}
          </div>
          <h4 className="admin-gpt-card-title">{g.name}</h4>
        </div>
      </div>
      <p className="admin-gpt-card-desc">{g.description}</p>
      <div className="admin-gpt-card-foot">
        <button onClick={onLock} className="btn btn-ghost btn-sm" data-testid={`admin-gpt-table-lock-toggle-${g.id}`}>
          {g.locked ? <><LockOpen size={14} weight="duotone" /> Unlock</> : <><Lock size={14} weight="duotone" /> Lock</>}
        </button>
        <div className="flex gap-2">
          <button onClick={onEdit} className="btn btn-ghost btn-sm" data-testid={`admin-gpt-edit-${g.id}`}>
            <PencilSimple size={14} weight="duotone" />
          </button>
          <button onClick={onDelete} className="btn btn-danger btn-sm" data-testid={`admin-gpt-delete-${g.id}`}>
            <Trash size={14} weight="duotone" />
          </button>
        </div>
      </div>
    </div>
  );
}

function GPTEditor({ gpt, onClose, onSaved }) {
  const isNew = !gpt;
  const [form, setForm] = useState(
    isNew
      ? { name: "", description: "", category: "General", avatar_url: "", system_prompt: "", conversation_starter: "", locked: false }
      : { ...gpt, system_prompt: gpt.system_prompt || "", avatar_url: gpt.avatar_url || "", conversation_starter: gpt.conversation_starter || "" }
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      if (isNew) await api.post("/gpts", form);
      else await api.patch(`/gpts/${gpt.id}`, form);
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid="admin-gpt-editor" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <p className="label-uppercase" style={{ color: "var(--accent)" }}>{isNew ? "New" : "Edit"}</p>
            <h3 className="modal-title">{isNew ? "Create custom GPT" : `Edit "${gpt.name}"`}</h3>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="admin-gpt-editor-close">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={onSave} className="modal-body space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-uppercase block mb-1.5">Name</label>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="admin-gpt-form-name" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Category</label>
              <input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required data-testid="admin-gpt-form-category" />
            </div>
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Description</label>
            <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="admin-gpt-form-description" />
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Avatar URL</label>
            <input className="input" value={form.avatar_url || ""} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://..." data-testid="admin-gpt-form-avatar" />
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">System prompt</label>
            <textarea
              className="textarea"
              rows={9}
              value={form.system_prompt}
              onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
              required
              data-testid="admin-gpt-form-prompt"
            />
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Conversation starter (optional)</label>
            <input
              className="input"
              value={form.conversation_starter || ""}
              onChange={(e) => setForm({ ...form, conversation_starter: e.target.value })}
              placeholder="e.g. Let's Start!!!"
              data-testid="admin-gpt-form-starter"
            />
          </div>
          <label className="toggle-row">
            <input
              type="checkbox"
              checked={!!form.locked}
              onChange={(e) => setForm({ ...form, locked: e.target.checked })}
              data-testid="admin-gpt-form-locked"
            />
            <span>Lock this GPT (hide from users)</span>
          </label>
          {err && <p className="error-banner" data-testid="admin-gpt-form-error">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button type="submit" className="btn btn-accent" disabled={saving} data-testid="admin-gpt-form-submit">
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Users section — search, status filter, table, drawer with GPT access +     */
/*  full chat inspection + JSON export.                                       */
/* -------------------------------------------------------------------------- */

function UsersSection({ users, loading = false, loadError = "", currentUserId, onChanged, isMaster = false }) {
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | suspended
  const [openUserId, setOpenUserId] = useState(null);

  const filtered = users.filter((u) => {
    const q = query.trim().toLowerCase();
    const matches = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    const status = u.is_active !== false ? "active" : "suspended";
    const matchesStatus = statusFilter === "all" || statusFilter === status;
    return matches && matchesStatus;
  });

  const openUser = filtered.find((u) => u.id === openUserId) || users.find((u) => u.id === openUserId);

  const toggleRole = async (u) => {
    if (u.id === currentUserId) {
      alert("You cannot change your own role.");
      return;
    }
    const newRole = u.role === "admin" ? "user" : "admin";
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: newRole });
      onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const toggleStatus = async (u) => {
    if (u.id === currentUserId) {
      alert("You cannot suspend yourself.");
      return;
    }
    const next = !(u.is_active !== false); // currently active → suspend
    try {
      await api.patch(`/admin/users/${u.id}/status`, { is_active: next });
      onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const onDelete = async (u) => {
    if (u.id === currentUserId) return alert("You cannot delete yourself.");
    if (!window.confirm(`Delete user ${u.email}? This also wipes all their conversations.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      if (openUserId === u.id) setOpenUserId(null);
      onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const onDownload = async (u) => {
    try {
      const { data } = await api.get(`/admin/users/${u.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `user-${(u.email || u.id).replace(/@/g, "_at_")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Export failed");
    }
  };

  const onDownloadHubDoc = async () => {
    try {
      await downloadBlob("/admin/docs/hub-working.zip", `Consultation_Hub_Working_${new Date().toISOString().slice(0, 10)}.zip`);
    } catch (e) {
      setError("Documentation download failed");
    }
  };

  const onExportUsageCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("search", query.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      const qs = params.toString();
      await downloadBlob(`/admin/export/usage.csv${qs ? `?${qs}` : ""}`, `gpt-usage-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) {
      setError("Export failed");
    }
  };

  const onExportMentorshipCsv = async () => {
    try {
      const res = await api.get(`/admin/export/mentorship.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mentorship-members-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Export failed");
    }
  };

  return (
    <div className="fade-up space-y-6" data-testid="admin-users-tab">
      <SectionHeader
        eyebrow="Members"
        title="Users"
        subtitle="Search, manage access, suspend, and inspect every chat — per user."
      />

      <div className="users-toolbar">
        <div className="admin-search w-full max-w-md">
          <MagnifyingGlass size={16} weight="bold" />
          <input
            placeholder="Search by name or email..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-users-search"
          />
        </div>
        <div className="status-filter" data-testid="admin-users-status-filter">
          {[
            { key: "all", label: "All" },
            { key: "active", label: "Active" },
            { key: "suspended", label: "Suspended" },
          ].map((opt) => (
            <button
              key={opt.key}
              className={`status-pill ${statusFilter === opt.key ? "is-active" : ""}`}
              onClick={() => setStatusFilter(opt.key)}
              data-testid={`admin-users-filter-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm"
            onClick={onExportMentorshipCsv}
            data-testid="admin-export-mentorship-csv"
            title="Download every mentorship-approved member with onboarding status and orientation answers"
          >
            <DownloadSimple size={14} weight="bold" /> Mentorship CSV
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={onExportUsageCsv}
            data-testid="admin-export-usage-csv"
            title="One row per participant × GPT: chats, messages, first/last used — for the users currently shown"
          >
            <DownloadSimple size={14} weight="bold" /> Usage CSV
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={onDownloadHubDoc}
            data-testid="admin-download-hub-doc"
            title="The complete working document of the hub: architecture, GPT catalogue, every system prompt (ZIP with a Markdown file)"
          >
            <DownloadSimple size={14} weight="bold" /> Hub documentation
          </button>
        </div>
      </div>

      {error && <p className="error-banner" data-testid="admin-users-error">{error}</p>}

      <div className="grid-frame overflow-x-auto">
        <table className="adm-table" data-testid="admin-users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last active</th>
              <th>Last GPT</th>
              <th style={{ textAlign: "right" }}>Convos</th>
              <th style={{ textAlign: "right" }}>Msgs</th>
              <th style={{ width: 1 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const isSuspended = u.is_active === false;
              return (
                <tr
                  key={u.id}
                  data-testid={`admin-user-row-${u.id}`}
                  onClick={() => setOpenUserId(u.id)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="user-avatar">{u.name.slice(0, 1).toUpperCase()}</div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{u.name}</span>
                        {u.id === currentUserId && <span className="chip chip-muted">You</span>}
                      </div>
                    </div>
                  </td>
                  <td style={{ color: "var(--muted)" }}>{u.email}</td>
                  <td>
                    {u.role === "admin" ? (
                      <span className="chip"><ShieldCheck size={10} weight="bold" />&nbsp;Admin</span>
                    ) : (
                      <span className="chip chip-muted">User</span>
                    )}
                  </td>
                  <td>
                    {isSuspended ? (
                      <span className="chip chip-danger">Suspended</span>
                    ) : (
                      <span className="chip chip-success">Active</span>
                    )}
                  </td>
                  <td style={{ color: "var(--muted)" }}>{formatRelative(u.last_active_at)}</td>
                  <td data-testid={`admin-user-last-gpt-${u.id}`}>
                    {u.last_gpt_name ? (
                      <div className="last-gpt-cell"><span className="last-gpt-name">{u.last_gpt_name}</span><span className="last-gpt-time">{formatRelative(u.last_message_at)}</span></div>
                    ) : (
                      <span style={{ color: "var(--muted)" }}>—</span>
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>{u.conversation_count ?? 0}</td>
                  <td style={{ textAlign: "right" }}>{u.message_count ?? 0}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setOpenUserId(u.id)}
                        className="btn btn-ghost btn-sm"
                        data-testid={`admin-user-view-${u.id}`}
                      >
                        View
                      </button>
                      {isMaster && (
                        <>
                          <button
                            onClick={() => toggleStatus(u)}
                            className={`btn btn-sm ${isSuspended ? "btn-accent" : "btn-ghost"}`}
                            disabled={u.id === currentUserId}
                            data-testid={`admin-user-toggle-status-${u.id}`}
                          >
                            {isSuspended ? "Activate" : "Suspend"}
                          </button>
                          <button
                            onClick={() => onDelete(u)}
                            className="btn btn-danger btn-sm"
                            disabled={u.id === currentUserId}
                            data-testid={`admin-user-delete-${u.id}`}
                            title="Delete user"
                          >
                            <Trash size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center py-10" style={{ color: loadError ? "var(--danger, #b42318)" : "var(--muted)" }} data-testid="users-table-empty">
                  {loading ? "Loading users…" : loadError || "No users found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openUser && (
        <UserDrawer
          key={openUser.id}
          user={openUser}
          currentUserId={currentUserId}
          onClose={() => setOpenUserId(null)}
          onChanged={onChanged}
          onToggleRole={() => toggleRole(openUser)}
          onToggleStatus={() => toggleStatus(openUser)}
          onDelete={() => onDelete(openUser)}
          onDownload={() => onDownload(openUser)}
          isMaster={isMaster}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  User drawer                                                               */
/* -------------------------------------------------------------------------- */

function UserDrawer({
  user,
  currentUserId,
  onClose,
  onChanged,
  onToggleRole,
  onToggleStatus,
  onDelete,
  onDownload,
  isMaster = false,
}) {
  // Default tab: when a user has filled the orientation form, jump straight
  // to the Onboarding tab so admins can see the answers without clicking
  // around. Otherwise show Activity stats.
  const orientationOnFile =
    !!user.mentorship_orientation_submitted_at ||
    Object.values(user.mentorship_orientation || {}).some(
      (v) => v !== null && v !== undefined && v !== ""
    );
  const [tab, setTab] = useState(orientationOnFile ? "onboarding" : "activity");
  const [conversations, setConversations] = useState(null);
  const [allGpts, setAllGpts] = useState([]);
  const [granted, setGranted] = useState(new Set(user.granted_gpt_ids || []));
  const [revoked, setRevoked] = useState(new Set(user.revoked_gpt_ids || []));
  const [savingAccess, setSavingAccess] = useState(false);
  const [accessError, setAccessError] = useState("");

  // Lock body scroll while drawer is open, and reset page scroll to top so
  // the drawer (which renders inside a transformed admin layout, breaking
  // position: fixed pinning) sits flush against the viewport top.
  useEffect(() => {
    const prev = document.body.style.overflow;
    const prevScroll = window.scrollY;
    document.body.style.overflow = "hidden";
    window.scrollTo(0, 0);
    return () => {
      document.body.style.overflow = prev;
      window.scrollTo(0, prevScroll);
    };
  }, []);

  // Load conversations once when opening
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${user.id}/conversations`);
        setConversations(data);
      } catch {
        setConversations([]);
      }
    })();
  }, [user.id]);

  // Load GPTs once (for the access matrix)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/gpts");
        setAllGpts(data);
      } catch {
        setAllGpts([]);
      }
    })();
  }, []);

  const setAccessState = (gptId, state) => {
    // state: "default" | "grant" | "revoke"
    const g = new Set(granted);
    const r = new Set(revoked);
    g.delete(gptId);
    r.delete(gptId);
    if (state === "grant") g.add(gptId);
    if (state === "revoke") r.add(gptId);
    setGranted(g);
    setRevoked(r);
  };

  const saveAccess = async () => {
    setSavingAccess(true);
    setAccessError("");
    try {
      await api.patch(`/admin/users/${user.id}/gpt-access`, {
        granted_gpt_ids: [...granted],
        revoked_gpt_ids: [...revoked],
      });
      onChanged();
    } catch (e) {
      setAccessError(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingAccess(false);
    }
  };

  const isSuspended = user.is_active === false;
  const isSelf = user.id === currentUserId;
  const isApproved = !!user.mentorship_approved;
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [approvalError, setApprovalError] = useState("");

  const toggleApproval = async () => {
    setApprovalSaving(true);
    setApprovalError("");
    try {
      await api.patch(`/admin/users/${user.id}/mentorship-approval`, {
        approved: !isApproved,
      });
      onChanged();
    } catch (e) {
      setApprovalError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update approval");
    } finally {
      setApprovalSaving(false);
    }
  };

  return createPortal(
    <div className="drawer-backdrop" onClick={onClose} data-testid="admin-user-drawer">
      <aside
        className="drawer-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="drawer-head">
          <div className="flex items-center gap-3 min-w-0">
            <div className="user-avatar user-avatar-lg">
              {user.name.slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="drawer-name truncate">{user.name}</div>
              <div className="drawer-mail truncate">{user.email}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            data-testid="admin-user-drawer-close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="drawer-meta">
          {user.role === "admin" ? (
            <span className="chip"><ShieldCheck size={10} weight="bold" />&nbsp;Admin</span>
          ) : (
            <span className="chip chip-muted">User</span>
          )}
          {isSuspended ? (
            <span className="chip chip-danger">Suspended</span>
          ) : (
            <span className="chip chip-success">Active</span>
          )}
          {user.role !== "admin" && (
            isApproved ? (
              <span className="chip chip-success" data-testid="drawer-mentorship-chip"><GraduationCap size={10} weight="bold" />&nbsp;Mentorship enrolled</span>
            ) : (
              <span className="chip chip-muted" data-testid="drawer-mentorship-chip"><GraduationCap size={10} weight="bold" />&nbsp;Not enrolled</span>
            )
          )}
          <span className="chip chip-muted">Joined {formatDate(user.created_at)}</span>
        </div>

        <div className="drawer-actions">
          {isMaster && (
            <>
              <button
                className={`btn btn-sm ${isSuspended ? "btn-accent" : "btn-ghost"}`}
                onClick={onToggleStatus}
                disabled={isSelf}
                data-testid="drawer-toggle-status"
              >
                {isSuspended ? "Activate" : "Suspend"}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={onToggleRole}
                disabled={isSelf}
                data-testid="drawer-toggle-role"
              >
                {user.role === "admin" ? "Demote to user" : "Promote to admin"}
              </button>
            </>
          )}
          {user.role !== "admin" && (
            <button
              className={`btn btn-sm ${isApproved ? "btn-ghost" : "btn-accent"}`}
              onClick={toggleApproval}
              disabled={approvalSaving}
              data-testid="drawer-toggle-mentorship"
              title={isApproved ? "Revoke mentorship enrollment (keeps T&C + Orientation on file so you can re-approve with one click)" : "Approve user for the 6 Months Mentorship program"}
            >
              <GraduationCap size={14} weight="bold" />&nbsp;
              {approvalSaving
                ? "Saving…"
                : isApproved
                  ? "Revoke mentorship"
                  : "Approve for mentorship"}
            </button>
          )}
          {isMaster && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={onDownload}
              data-testid="drawer-download"
            >
              Download JSON
            </button>
          )}
          {isMaster && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={async () => {
                const pwd = window.prompt(
                  `Reset password for ${user.email}.\n\nEnter the new password (min 6 chars). The user will be able to sign in with this immediately:`,
                  ""
                );
                if (!pwd) return;
                if (pwd.length < 6) { alert("Password must be at least 6 characters."); return; }
                try {
                  await api.post(`/admin/users/${user.id}/reset-password`, { new_password: pwd });
                  alert(`Password updated for ${user.email}.`);
                } catch (e) {
                  alert(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reset password");
                }
              }}
              data-testid="drawer-reset-password"
              title="Force-reset this user's password"
            >
              Reset password
            </button>
          )}
          {isMaster && (
            <button
              className="btn btn-danger btn-sm"
              onClick={onDelete}
              disabled={isSelf}
              data-testid="drawer-delete"
            >
              <Trash size={14} />&nbsp;Delete
            </button>
          )}
        </div>
        {approvalError && (
          <p className="error-banner" data-testid="drawer-mentorship-error" style={{ margin: "0 1.25rem 0.5rem" }}>
            {approvalError}
          </p>
        )}

        <div className="drawer-tabs" role="tablist">
          {[
            { key: "activity", label: "Activity", masterOnly: false },
            { key: "access", label: "GPT Access", masterOnly: true },
            { key: "chats", label: `Chats${conversations ? ` (${conversations.length})` : ""}`, masterOnly: false },
            {
              key: "onboarding",
              label: orientationOnFile ? "Onboarding ●" : "Onboarding",
              masterOnly: false,
            },
            { key: "nichedoc", label: "Niche Doc", masterOnly: false },
            { key: "bootcamps", label: "Bootcamps", masterOnly: false },
          ].filter((t) => isMaster || !t.masterOnly).map((t) => (
            <button
              key={t.key}
              className={`drawer-tab ${tab === t.key ? "is-active" : ""}`}
              onClick={() => setTab(t.key)}
              data-testid={`drawer-tab-${t.key}`}
              title={t.key === "onboarding" && orientationOnFile ? "Orientation form submitted" : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="drawer-body">
          {tab === "activity" && (
            <UserActivityTab user={user} conversations={conversations} />
          )}
          {tab === "access" && (
            <UserAccessTab
              allGpts={allGpts}
              granted={granted}
              revoked={revoked}
              setAccessState={setAccessState}
              onSave={saveAccess}
              saving={savingAccess}
              error={accessError}
            />
          )}
          {tab === "chats" && <UserChatsTab user={user} />}
          {tab === "onboarding" && (
            <UserOnboardingTab user={user} />
          )}
          {tab === "nichedoc" && (
            <UserNicheDocTab user={user} />
          )}
          {tab === "bootcamps" && (
            <UserBootcampsTab user={user} />
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}

function UserNicheDocTab({ user }) {
  const [doc, setDoc] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/users/${user.id}/niche-doc`);
      setDoc(data.doc);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load document");
      setDoc(null);
    }
  };
  useEffect(() => { load(); }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async () => {
    if (!window.confirm("Delete this participant's Niche Recommendation and its extracted profile? The Editor will go back to the full interview.")) return;
    setBusy(true);
    setErr("");
    try {
      await api.delete(`/admin/users/${user.id}/niche-doc`);
      setDoc(null);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete document");
    } finally {
      setBusy(false);
    }
  };

  const openFile = async () => {
    try {
      const res = await api.get(`/admin/users/${user.id}/niche-doc/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setErr("Could not open the file");
    }
  };

  if (doc === undefined) {
    return <div className="py-10 flex justify-center" data-testid="drawer-nichedoc-loading"><span className="spinner" /></div>;
  }
  return (
    <div className="space-y-4" data-testid="drawer-nichedoc">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold">Niche Recommendation</h3>
          <p className="text-xs" style={{ color: "var(--muted)" }}>
            Read by Claude and used by the Editorial Website Builder. Read-only.
          </p>
        </div>
        {doc ? (
          <span className="chip chip-success" data-testid="drawer-nichedoc-status">On file · {formatDateTime(doc.uploaded_at)}</span>
        ) : (
          <span className="chip chip-muted" data-testid="drawer-nichedoc-status">Not uploaded</span>
        )}
      </div>
      {err && <p className="error-banner" data-testid="drawer-nichedoc-error">{err}</p>}
      {doc && (
        <>
          <div className="text-xs flex flex-wrap gap-x-4 gap-y-1" style={{ color: "var(--muted)" }}>
            <span>File: {doc.original_filename || "—"}</span>
            <span>Versions: {doc.versions}</span>
            <span>Name check: {doc.name_match}</span>
            <span>Participant on doc: {doc.extracted?.participant_name || "—"}</span>
          </div>
          <NicheProfileCards profile={doc.profile} confidence={doc.confidence} testIdPrefix="admin-niche" />
          {(doc.extracted?.assessment || doc.extracted?.starting_point || doc.extracted?.first_actions?.length) && (
            <div className="niche-card" data-testid="admin-niche-private">
              <div className="niche-card-head">
                <span className="label-uppercase">Private — never sent to the website GPT</span>
              </div>
              {doc.extracted?.starting_point?.summary && <p><strong>Starting point:</strong> {doc.extracted.starting_point.summary}</p>}
              {doc.extracted?.assessment?.pattern && <p><strong>Pattern:</strong> {doc.extracted.assessment.pattern}</p>}
              {doc.extracted?.assessment?.gaps?.length > 0 && <p><strong>Gaps:</strong> {doc.extracted.assessment.gaps.join("; ")}</p>}
              {doc.extracted?.reframe?.stop_calling && <p><strong>Stop calling:</strong> {doc.extracted.reframe.stop_calling}</p>}
              {doc.extracted?.first_actions?.length > 0 && <p><strong>First actions:</strong> {doc.extracted.first_actions.join(" · ")}</p>}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <button type="button" className="btn btn-sm" onClick={openFile} data-testid="drawer-nichedoc-open">Open file</button>
            <button type="button" className="btn btn-danger btn-sm" onClick={remove} disabled={busy} data-testid="drawer-nichedoc-delete">
              {busy ? "Deleting…" : "Delete document"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function UserActivityTab({ user, conversations }) {
  const lastActive = formatRelative(user.last_active_at);
  const lastLogin = formatRelative(user.last_login_at);
  return (
    <div className="space-y-5" data-testid="drawer-activity">
      <div className="stat-grid">
        <StatCard label="Total chats" value={user.conversation_count ?? 0} />
        <StatCard label="Total messages" value={user.message_count ?? 0} />
        <StatCard label="Last active" value={lastActive} small />
        <StatCard label="Last login" value={lastLogin} small />
      </div>
      <div>
        <p className="label-uppercase mb-2">GPTs used</p>
        {user.gpts_used && user.gpts_used.length > 0 ? (
          <div className="chip-row">
            {user.gpts_used.map((n) => (
              <span key={n} className="chip chip-muted">{n}</span>
            ))}
          </div>
        ) : (
          <p className="text-sm" style={{ color: "var(--muted)" }}>
            Hasn’t opened any GPT yet.
          </p>
        )}
      </div>
      {conversations && conversations.length > 0 && (
        <div>
          <p className="label-uppercase mb-2">Recent activity</p>
          <ul className="activity-list">
            {conversations.slice(0, 5).map((c) => (
              <li key={c.id} className="activity-row">
                <div className="activity-meta">
                  <div className="activity-title">{c.title}</div>
                  <div className="activity-sub">
                    {c.gpt_name} · {c.message_count} msg{c.message_count === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="activity-time">{formatRelative(c.updated_at)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UserAccessTab({ allGpts, granted, revoked, setAccessState, onSave, saving, error }) {
  // Sort: 6 Months Mentorship first, then Action Program, then others; alphabetical within
  const sorted = [...allGpts].sort((a, b) => {
    const order = (g) => (g.category === "6 Months Mentorship" ? 0 : g.category === "Action Program" ? 1 : 2);
    if (order(a) !== order(b)) return order(a) - order(b);
    return a.name.localeCompare(b.name);
  });
  return (
    <div className="space-y-4" data-testid="drawer-access">
      <p className="text-sm" style={{ color: "var(--muted)" }}>
        <strong>Default</strong> follows the GPT’s built-in lock.
        <strong> Grant</strong> always lets this user in (even if locked).
        <strong> Revoke</strong> always blocks them (even if unlocked).
      </p>
      {error && <p className="error-banner">{error}</p>}
      <div className="access-list">
        {sorted.map((g) => {
          const isGranted = granted.has(g.id);
          const isRevoked = revoked.has(g.id);
          const state = isRevoked ? "revoke" : isGranted ? "grant" : "default";
          return (
            <div key={g.id} className="access-row" data-testid={`access-row-${g.id}`}>
              <div className="access-meta">
                <div className="access-name">{g.name}</div>
                <div className="access-sub">{g.category}</div>
              </div>
              <div className="access-pills" role="radiogroup">
                {[
                  { k: "default", label: "Default" },
                  { k: "grant", label: "Grant" },
                  { k: "revoke", label: "Revoke" },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    className={`access-pill access-pill-${opt.k} ${state === opt.k ? "is-active" : ""}`}
                    onClick={() => setAccessState(g.id, opt.k)}
                    data-testid={`access-${opt.k}-${g.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-end">
        <button
          className="btn btn-accent"
          onClick={onSave}
          disabled={saving}
          data-testid="drawer-save-access"
        >
          {saving ? "Saving..." : "Save access"}
        </button>
      </div>
    </div>
  );
}

const ORIENTATION_LABELS = {
  full_name: "Full name",
  city: "City / location",
  current_role: "Current role / designation",
  industry: "Industry / domain",
  years_experience: "Years of experience",
  professional_stage: "Professional stage",
  consulting_consideration_period: "Considering consulting for",
  commitment_trigger: "What made them commit",
  current_monthly_income: "Current monthly income",
  target_income_6m: "Target income (6 months)",
  target_income_2y: "Target income (2 years)",
  success_vision: "Vision of success",
  practice_type: "Practice they want to build",
  rating_niche_clarity: "Niche clarity (1–5)",
  rating_offer_pricing: "Offer & pricing (1–5)",
  rating_problem_solving: "Problem solving (1–5)",
  rating_executive_communication: "Executive communication (1–5)",
  rating_personal_brand: "Personal brand (1–5)",
  rating_client_acquisition: "Client acquisition (1–5)",
  rating_sales_conversations: "Sales conversations (1–5)",
  rating_negotiation: "Negotiation (1–5)",
  weakest_area_concern: "Weakest-area concern",
  held_back_reason: "What held them back",
  biggest_fear: "Biggest fear",
  note_for_coach: "Note for the coach",
  why_will_succeed: "Why they'll succeed",
};

const ORIENTATION_SECTIONS = [
  { title: "About You", keys: ["full_name", "city", "current_role", "industry", "years_experience", "professional_stage", "consulting_consideration_period", "commitment_trigger"] },
  { title: "Income & Ambition", keys: ["current_monthly_income", "target_income_6m", "target_income_2y", "success_vision", "practice_type"] },
  { title: "Self-Assessment", keys: ["rating_niche_clarity", "rating_offer_pricing", "rating_problem_solving", "rating_executive_communication", "rating_personal_brand", "rating_client_acquisition", "rating_sales_conversations", "rating_negotiation"] },
  { title: "Gaps & Blockers", keys: ["weakest_area_concern", "held_back_reason", "biggest_fear"] },
  { title: "Note for Coach", keys: ["note_for_coach", "why_will_succeed"] },
];

function UserOnboardingTab({ user }) {
  const approved = !!user.mentorship_approved;
  const approvedAt = user.mentorship_approved_at;
  const tncAcceptedAt = user.mentorship_tnc_accepted_at;
  const tncVersion = user.mentorship_tnc_version;
  const orientationSubmittedAt = user.mentorship_orientation_submitted_at;
  const orientation = user.mentorship_orientation || {};

  const hasAny = Object.values(orientation).some((v) => v !== null && v !== undefined && v !== "");
  const [downloading, setDownloading] = useState(false);

  const downloadOrientation = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/users/${user.id}/orientation/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (user.email || user.id).replace("@", "_at_");
      a.download = `orientation-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || "Could not download orientation");
    } finally {
      setDownloading(false);
    }
  };

  const approvalStatus = approved
    ? { label: "Enrolled", time: approvedAt ? formatDateTime(approvedAt) : "—", cls: "chip-success" }
    : { label: "Not enrolled", time: "—", cls: "chip-muted" };
  const tncStatus = tncAcceptedAt
    ? { label: `Accepted${tncVersion ? ` (v${tncVersion})` : ""}`, time: formatDateTime(tncAcceptedAt), cls: "chip-success" }
    : { label: "Not accepted", time: "—", cls: "chip-danger" };
  const oriStatus = orientationSubmittedAt
    ? { label: "Submitted", time: formatDateTime(orientationSubmittedAt), cls: "chip-success" }
    : hasAny
      ? { label: "Draft in progress", time: "—", cls: "chip-muted" }
      : { label: "Not started", time: "—", cls: "chip-danger" };

  return (
    <div className="space-y-5" data-testid="drawer-onboarding">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold">Mentorship onboarding</h3>
          <p className="text-xs" style={{ color: "var(--muted)" }}>
            T&amp;C acceptance + 26-question orientation form responses.
          </p>
        </div>
        {hasAny && (
          <button
            type="button"
            className="btn btn-accent btn-sm"
            onClick={downloadOrientation}
            disabled={downloading}
            data-testid="drawer-download-orientation"
          >
            <Download size={14} weight="bold" />&nbsp;
            {downloading ? "Preparing…" : "Download form"}
          </button>
        )}
      </div>
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-card-label">Mentorship enrollment</div>
          <div className="mt-1 flex items-center gap-2">
            <span className={`chip ${approvalStatus.cls}`}>{approvalStatus.label}</span>
          </div>
          <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{approvalStatus.time}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Terms & Conditions</div>
          <div className="mt-1 flex items-center gap-2">
            <span className={`chip ${tncStatus.cls}`}>{tncStatus.label}</span>
          </div>
          <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{tncStatus.time}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Orientation form</div>
          <div className="mt-1 flex items-center gap-2">
            <span className={`chip ${oriStatus.cls}`}>{oriStatus.label}</span>
          </div>
          <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{oriStatus.time}</div>
        </div>
      </div>

      {!hasAny ? (
        <p className="text-sm py-6 text-center" style={{ color: "var(--muted)" }}>
          {user.name} hasn&apos;t filled the orientation form yet.
        </p>
      ) : (
        <div className="onboarding-readout" data-testid="drawer-onboarding-readout">
          {ORIENTATION_SECTIONS.map((sec) => (
            <section key={sec.title} className="onboarding-readout-section">
              <h4 className="onboarding-readout-title">{sec.title}</h4>
              <dl className="onboarding-readout-list">
                {sec.keys.map((k) => {
                  const v = orientation[k];
                  return (
                    <div key={k} className="onboarding-readout-row" data-testid={`onboarding-readout-${k}`}>
                      <dt>{ORIENTATION_LABELS[k] || k}</dt>
                      <dd>
                        {v === null || v === undefined || v === "" ? (
                          <span style={{ color: "var(--muted)" }}>—</span>
                        ) : typeof v === "string" && v.length > 80 ? (
                          <pre className="onboarding-readout-long">{v}</pre>
                        ) : (
                          <span>{v}</span>
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, small = false }) {
  return (
    <div className={`stat-card ${small ? "stat-card-sm" : ""}`}>
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-label">{label}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Date helpers                                                              */
/* -------------------------------------------------------------------------- */

function formatRelative(iso) {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

/* -------------------------------------------------------------------------- */
/* Password Resets section — master admin only. Shows pending /forgot-password
   tickets and lets the admin issue a temporary password + open WhatsApp
   pre-filled with the credential message. */
function PasswordResetsSection() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/password-reset-requests${tab ? `?status=${tab}` : ""}`);
        setRequests(data);
      } catch {
        setRequests([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [tab, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <section className="fade-up space-y-6" data-testid="password-resets-section">
      <header>
        <h1 className="text-2xl font-bold">Password Reset Requests</h1>
        <p className="text-sm" style={{ color: "var(--muted)" }}>
          Users who tapped &ldquo;Forgot password?&rdquo; on the login screen show up here. Set a temporary password, then click WhatsApp to send it directly to them.
        </p>
      </header>

      <div className="flex gap-2">
        {[
          { k: "pending", l: "Pending" },
          { k: "resolved", l: "Resolved" },
          { k: "rejected", l: "Rejected" },
          { k: "", l: "All" },
        ].map((t) => (
          <button
            key={t.k || "all"}
            className={`chip-pill ${tab === t.k ? "is-on" : ""}`}
            onClick={() => setTab(t.k)}
            data-testid={`reset-filter-${t.k || "all"}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><div className="spinner" /></div>
      ) : requests.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="resets-empty">
          No requests in this view.
        </p>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <ResetRequestRow key={r.id} req={r} onChanged={refresh} />
          ))}
        </div>
      )}
    </section>
  );
}

function ResetRequestRow({ req, onChanged }) {
  const [busy, setBusy] = useState(false);

  const generatePassword = () => {
    const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let pwd = "";
    for (let i = 0; i < 10; i++) pwd += charset[Math.floor(Math.random() * charset.length)];
    return pwd;
  };

  const resolve = async () => {
    const suggested = generatePassword();
    const pwd = window.prompt(
      `Set a temporary password for ${req.user_email}.\n\nThe user will sign in with this and change it.\n\nMin 6 characters:`,
      suggested,
    );
    if (!pwd) return;
    if (pwd.length < 6) { alert("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/password-reset-requests/${req.id}/resolve`, { temp_password: pwd });
      const name = data.user.name || req.user_name || "there";
      const msg = `Hi ${name}, your temporary password is the following: ${data.temp_password}. Use this to login and reset your password.`;
      // wa.me without a phone opens WhatsApp with the text prefilled; admin
      // picks the contact in their own WhatsApp app.
      const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      onChanged();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || "Failed to resolve request");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!window.confirm(`Reject the password reset request from ${req.user_email}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/password-reset-requests/${req.id}/reject`);
      onChanged();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reject");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="stat-card"
      style={{ padding: "1rem 1.25rem" }}
      data-testid={`reset-request-${req.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="text-base font-semibold">{req.user_name || req.user_email}</div>
          <div className="text-xs" style={{ color: "var(--muted)" }}>{req.user_email}</div>
          {req.note && (
            <p className="text-sm mt-2" style={{ color: "var(--foreground)" }}>
              &ldquo;{req.note}&rdquo;
            </p>
          )}
          <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>
            Requested {formatDateTime(req.created_at)}
            {req.resolved_at && ` · Resolved ${formatDateTime(req.resolved_at)} by ${req.resolved_by || "—"}`}
          </div>
        </div>
        <div className="flex gap-2">
          {req.status === "pending" ? (
            <>
              <button
                className="btn btn-accent btn-sm"
                onClick={resolve}
                disabled={busy}
                data-testid={`reset-resolve-${req.id}`}
                title="Set a temp password and open WhatsApp"
              >
                <WhatsappLogo size={14} weight="bold" />&nbsp;Set password &amp; WhatsApp
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={reject}
                disabled={busy}
                data-testid={`reset-reject-${req.id}`}
              >
                Reject
              </button>
            </>
          ) : (
            <span className={`chip ${req.status === "resolved" ? "chip-success" : "chip-danger"}`}>
              {req.status}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
